import { useTranslation } from "react-i18next";
import {
  AccountDeletionFailure,
  resolveAccountDeletionFailure,
} from "src/domains/myPageContent/accountInfo/user-query/accountDeletion";
import { useDeleteUser } from "src/domains/myPageContent/accountInfo/user-query/user.queries";
import { useSignOut } from "src/domains/shared/hooks/useSignOut";
import notice from "src/domains/shared/utils/notice";

export interface WithdrawAccountHandlers {
  /** 탈퇴가 거절·실패했을 때 — 분류(409 소유 프로젝트 / Cognito 재시도 / 그 밖)는 `resolveAccountDeletionFailure`. */
  onFailure?: (failure: AccountDeletionFailure) => void;
}

/**
 * 회원 탈퇴 — `DELETE /v1/users/me`. 204 면 완료 안내 뒤 로그아웃 흐름(`useSignOut`)으로 잇는다(이미 끝난 탈퇴의
 * 재호출도 204). 실패는 토스트 대신 `onFailure` 로 넘긴다 — 창 안에서 서버 문구(409)·재시도 안내(Cognito 400)를 보여야
 * 재시도 경로가 남는다.
 *
 * `useAuth` 에서 분리했다 — 탈퇴의 유일한 소비자(`useAccountWithdrawal`)가 로그인 상태(마운트마다 GetUser 호출·기기
 * 충돌 창·로케일)를 떠안지 않게. 로그아웃 뒤 `useAuth` 인스턴스의 상태 비우기는 화면 이동이 대신한다.
 */
export const useWithdrawAccount = () => {
  const { t } = useTranslation("common");
  const signOut = useSignOut();
  const {
    mutate: deleteAccount,
    isLoading: isDeleteRequestPending,
    isSuccess: isAccountDeleted,
  } = useDeleteUser();

  const withdrawAccount = (handlers: WithdrawAccountHandlers = {}) => {
    deleteAccount(undefined, {
      onSuccess: () => {
        notice("success", t("myPage.msg.accountDeleted", { ns: "common" }));
        // 로그아웃 실패는 종전(useAuth.signOut)처럼 로그만 남기고 삼킨다 — 탈퇴는 이미 끝났고, 창은
        // isWithdrawingAccount 가 잠근다. 흐름은 reject 하지 않으므로 기다리지 않고 띄운다.
        void signOut({
          onFailed: (error) => {
            console.log(error);
          },
        });
      },
      onError: (err) => {
        handlers.onFailure?.(resolveAccountDeletionFailure(err));
      },
    });
  };

  // 성공(204)도 진행 중으로 친다 — react-query 는 mutate 의 onSuccess 를 부를 때 이미 isLoading 을 내려 두므로,
  // 요청 중만 보면 onSuccess 가 잇는 비동기 signOut()(자리 해제 최대 3초 → Amplify 로그아웃 → 이동) 동안 창이 풀려
  // 탈퇴를 다시 부를 수 있다.
  const isWithdrawingAccount = isDeleteRequestPending || isAccountDeleted;

  return {
    withdrawAccount,
    /**
     * 탈퇴 흐름이 진행 중 — `withdrawAccount()` 를 부른 뒤 DELETE 가 실패하거나 화면이 떠날 때까지 true 다.
     * 204 뒤에는 false 로 돌아오지 않는다 — 로그아웃 흐름이 화면을 옮기는 동안에도 true 이고, 그 흐름이 실패해
     * 화면이 남아도 그대로다. 확인 창의 처리 중 표시·잠금에 쓴다.
     */
    isWithdrawingAccount,
  };
};
